import { useState, useEffect } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { customersAPI } from "../../services/api";
import CustomerList from "./CustomerList";
import CustomerProfile from "./CustomerProfile";
import AddCustomer from "./AddCustomer";
import {
  Users, UserPlus, LayoutDashboard, TrendingUp,
  TrendingDown, DollarSign, AlertTriangle, RefreshCw,
} from "lucide-react";

type Tab = "dashboard" | "customers" | "add";

const fmt = (n: any) => new Intl.NumberFormat("en-US").format(Math.abs(Number(n) || 0));

export default function Customers() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const [tab, setTab]             = useState<Tab>("dashboard");
  const [stats, setStats]         = useState<any>(null);
  const [loading, setLoading]     = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [search, setSearch]       = useState("");

  useEffect(() => { if (tab === "dashboard") loadDashboard(); }, [tab]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const r = await customersAPI.dashboard();
      if (r.success) setStats(r);
    } catch {}
    setLoading(false);
  };

  const tabs = [
    { id: "dashboard", label: "داشبورد",    icon: LayoutDashboard },
    { id: "customers", label: "مشتریان",    icon: Users },
    { id: "add",       label: "مشتری جدید", icon: UserPlus },
  ];

  if (selectedId) {
    return <CustomerProfile customerId={selectedId} onBack={() => { setSelectedId(null); }} />;
  }

  return (
    <div style={{ padding: "28px 28px 48px", fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }} dir="rtl">

      {/* هدر */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 11, color: "#8b5cf6", fontWeight: 700, letterSpacing: "1.5px", marginBottom: 6 }}>🤝 مشتریان</div>
        <h1 style={{ fontSize: 26, fontWeight: 900, margin: 0, color: T.text }}>مدیریت مشتریان</h1>
        <p style={{ fontSize: 13, color: T.subtext, margin: "5px 0 0" }}>ثبت، پیگیری بیلانس و گزارش‌گیری مشتریان</p>
      </div>

      {/* تب‌ها */}
      <div style={{ display: "flex", gap: 8, marginBottom: 28, background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)", border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 6 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id as Tab)} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "10px 16px", borderRadius: 10, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit", background: tab === t.id ? "linear-gradient(135deg,#8b5cf6,#6d28d9)" : "transparent", color: tab === t.id ? "white" : T.subtext, transition: "all 0.2s" }}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {/* ── داشبورد ── */}
      {tab === "dashboard" && (
        loading ? (
          <div style={{ textAlign: "center", padding: 80, color: T.subtext }}>
            <div style={{ width: 40, height: 40, border: "3px solid rgba(139,92,246,0.2)", borderTop: "3px solid #8b5cf6", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
            در حال بارگذاری...
          </div>
        ) : (
          <div>
            {/* کارت‌های آمار */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 16, marginBottom: 28 }}>
              {[
                { label: "کل مشتریان فعال",  val: fmt(stats?.stats?.total),        color: "#8b5cf6", icon: Users,         suffix: "مشتری" },
                { label: "قرضداران ما",       val: fmt(stats?.stats?.debtors),      color: "#ef4444", icon: TrendingUp,    suffix: "مشتری" },
                { label: "طلبکاران ما",     val: fmt(stats?.stats?.creditors),    color: "#10b981", icon: TrendingDown,  suffix: "مشتری" },
                { label: "جمع طلب از مشتریان", val: fmt(stats?.stats?.total_debt),  color: "#f59e0b", icon: DollarSign,    suffix: "" },
                { label: "جمع قرضداری به مشتریان", val: fmt(stats?.stats?.total_credit), color: "#3b82f6", icon: AlertTriangle, suffix: "" },
              ].map((card, i) => (
                <div key={i} style={{ borderRadius: 16, padding: 20, background: dark ? "rgba(255,255,255,0.03)" : "#fff", border: `1px solid ${T.cardBorder}`, borderTop: `3px solid ${card.color}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: `${card.color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <card.icon size={17} color={card.color} />
                    </div>
                    <div style={{ fontSize: 11, color: T.subtext, fontWeight: 600 }}>{card.label}</div>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: card.color }}>{card.val}</div>
                  {card.suffix && <div style={{ fontSize: 11, color: T.subtext, marginTop: 2 }}>{card.suffix}</div>}
                </div>
              ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              {/* بیشترین طلب */}
              <div style={{ borderRadius: 16, padding: 20, background: dark ? "rgba(255,255,255,0.03)" : "#fff", border: `1px solid ${T.cardBorder}` }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 16 }}>🔴 بیشترین طلب از مشتریان</div>
                {(stats?.top_debtors || []).length === 0
                  ? <div style={{ color: T.subtext, fontSize: 13, textAlign: "center", padding: 20 }}>هیچ طلبی ثبت نشده</div>
                  : (stats?.top_debtors || []).map((c: any, i: number) => (
                    <div key={i} onClick={() => setSelectedId(c.id)}
                      style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 10px", borderBottom: `1px solid ${T.cardBorder}`, cursor: "pointer", borderRadius: 8, transition: "background 0.15s" }}
                      onMouseEnter={e => (e.currentTarget.style.background = dark ? "rgba(239,68,68,0.05)" : "rgba(239,68,68,0.03)")}
                      onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: "linear-gradient(135deg,#ef4444,#dc2626)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "white", fontWeight: 700 }}>
                          {i + 1}
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{c.first_name} {c.last_name}</div>
                          <div style={{ fontSize: 10, color: T.subtext }}>{c.code}</div>
                        </div>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: "#ef4444" }}>{fmt(c.current_balance)} {c.currency}</div>
                    </div>
                  ))
                }
              </div>

              {/* آخرین مشتریان */}
              <div style={{ borderRadius: 16, padding: 20, background: dark ? "rgba(255,255,255,0.03)" : "#fff", border: `1px solid ${T.cardBorder}` }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 16 }}>🆕 آخرین مشتریان ثبت شده</div>
                {(stats?.recent || []).map((c: any, i: number) => (
                  <div key={i} onClick={() => setSelectedId(c.id)}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 8px", borderBottom: `1px solid ${T.cardBorder}`, cursor: "pointer", borderRadius: 8, transition: "background 0.15s" }}
                    onMouseEnter={e => (e.currentTarget.style.background = dark ? "rgba(139,92,246,0.05)" : "rgba(139,92,246,0.03)")}
                    onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "white", fontWeight: 700 }}>
                        {c.first_name?.[0]}{c.last_name?.[0] || ""}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{c.first_name} {c.last_name}</div>
                        <div style={{ fontSize: 10, color: T.subtext }}>{c.code} {c.company_name ? `• ${c.company_name}` : ""}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: "left" }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: Number(c.current_balance) >= 0 ? "#ef4444" : "#10b981" }}>
                        {Number(c.current_balance) >= 0 ? "+" : ""}{fmt(c.current_balance)} {c.currency}
                      </div>
                      <div style={{ fontSize: 10, color: Number(c.current_balance) > 0 ? "#ef4444" : Number(c.current_balance) < 0 ? "#10b981" : T.subtext }}>
                        {Number(c.current_balance) > 0 ? "قرضدار" : Number(c.current_balance) < 0 ? "طلبکار" : "تسویه"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      )}

      {tab === "customers" && (
        <CustomerList initialSearch={search} onSelect={id => setSelectedId(id)} />
      )}

      {tab === "add" && (
        <AddCustomer onSuccess={() => { setTab("customers"); loadDashboard(); }} />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}